import * as React from 'react';

import { PaywallScreens } from '~analytics/types';
import { Container } from '~shared/components/Container';
import { Spinner } from '~shared/components/Spinner';
import {
  EditOrSaveButton,
  TitleOrEditQuizTitle,
} from '~teacher/components/QuizEditor/QuizEditorHeader';
import { QuizEditorIsUploadingImageContext } from '~teacher/components/QuizEditor/QuizEditorIsUploadingImage';
import { OfficialLibraryBadge } from '~teacher/components/workspaces/OfficialLibraryBadge';
import { SharedLibraryUpgradeBanner } from '~teacher/components/workspaces/SharedLibraryUpgradeBanner';
import { WorkspaceQuizzesBreadcrumbs } from '~teacher/components/workspaces/WorkspaceQuizzes/WorkspaceQuizzesBreadcrumbs';
import { WorkspaceQuizLockBanner } from '~teacher/components/WorkspacesQuiz';
import { useWorkspaceActionPermissions } from '~teacher/hook/workspaces/settings';
import { useGetWorkspaceQuiz } from '~teacher/hook/workspaces/useGetWorkspaceQuiz';
import { proCheck } from '~teacher/util/proCheck';

interface Props {
  isControlsDisabled: boolean;
  onHandleSaveAndExit: () => Promise<void>;
  workspaceId: string;
  workspaceQuizId: string;
}

export function WorkspaceQuizEditorHeader({
  isControlsDisabled,
  onHandleSaveAndExit,
  workspaceId,
  workspaceQuizId,
}: Props) {
  const { canEditQuiz } = useWorkspaceActionPermissions({ workspaceId });
  const canUseSharedLibraryQuizzes = proCheck.canUseSharedLibraryQuizzes();
  const currentQuiz = useGetWorkspaceQuiz({
    workspaceQuizId,
    workspaceId,
    select: (workspaceQuizResponse) => {
      return {
        ...workspaceQuizResponse.quiz,
        id: `${workspaceQuizResponse.id}`,
        parentId: workspaceQuizResponse.parentFolderId,
        deletedDate: workspaceQuizResponse.deletedDate,
        workspaceId: workspaceQuizResponse.workspaceId,
        type: 'quiz' as const,
        lockId: workspaceQuizResponse.lockId,
        publishedBy: workspaceQuizResponse.publishedBy,
        parentFolderId: workspaceQuizResponse.parentFolderId,
        publishedDate: workspaceQuizResponse.publishedDate,
        owner: workspaceQuizResponse.owner,
        quizId: workspaceQuizResponse.quiz.id,
      };
    },
  });

  if (!currentQuiz.data) {
    return <Spinner />;
  }

  return (
    <>
      <WorkspaceQuizLockBanner
        workspaceQuizId={workspaceQuizId}
        workspaceId={workspaceId}
      />
      <SharedLibraryUpgradeBanner
        canUseSharedLibraryQuizzes={canUseSharedLibraryQuizzes}
        paywallScreen={PaywallScreens.SharedLibraryEditQuiz}
      />
      <Container row className={'p-5'}>
        <WorkspaceQuizzesBreadcrumbs
          hideCurrent
          currentItem={currentQuiz.data}
          workspaceId={workspaceId}
        />
      </Container>
      <OfficialLibraryBadge workspaceId={workspaceId} className="px-5" />
      <div
        className="flex flex-col-reverse md:flex-row md:items-center md:justify-between py-6 md:py-12"
        data-quiz-title
      >
        <TitleOrEditQuizTitle
          workspaceQuizId={workspaceQuizId}
          workspaceId={workspaceId}
          isControlsDisabled={isControlsDisabled}
        />
        <QuizEditorIsUploadingImageContext.Consumer>
          {({ isUploadingImages }) => (
            <div className="flex flex-row items-center pl-5 pb-4 md:pb-0 md:pl-0">
              <EditOrSaveButton
                canEditQuiz={canEditQuiz}
                onHandleSaveAndExit={onHandleSaveAndExit}
                workspaceId={workspaceId}
                workspaceQuizId={workspaceQuizId}
                isUploadingImages={isUploadingImages}
                isControlsDisabled={isControlsDisabled}
              />
            </div>
          )}
        </QuizEditorIsUploadingImageContext.Consumer>
      </div>
    </>
  );
}
